import { ListGroup, ListGroupItem, Row } from "reactstrap";
import { StickyMainHeader } from "../../header/StickyMainHeader";
import { ReactNode, useEffect, useRef, useState } from "react";
import { CyPlaylistIndexTable } from "../../../playlists/CyPlaylistIndexTable";
import { Entry } from "./Entry";
import { HeaderEntryCheckboxColumn } from "./HeaderEntryCheckboxColumn";
import { isHotkeyPressed } from "react-hotkeys-hook";
import { LoadingIndicator } from "../../LoadingIndicator";

interface Props<T> {
  headerFirstRow?: ReactNode;
  headerColumns: ReactNode;
  data: readonly T[];
  renderElement: (entry: T) => ReactNode;
  linkBuilder?: (entry: T) => string;
  onClick?: (entry: T) => void;
  keyExtractor: (entry: T) => string;
  setSelectedEntries?: (value: T[]) => void;
  loading?: boolean;
}

export function MultiSelectableList<T>({
  headerFirstRow,
  headerColumns,
  data,
  renderElement,
  linkBuilder,
  onClick,
  keyExtractor,
  setSelectedEntries,
  loading = false,
}: Props<T>) {
  const [latestSelectedIdentifier, setLatestSelectedIdentifier] = useState<
    string | undefined
  >(undefined);

  const [selectionList, setSelectionList] = useState<string[]>([]);

  // Refs to avoid dependency issues in useEffect
  const dataRef = useRef(data);
  const keyExtractorRef = useRef(keyExtractor);
  const setSelectedEntriesRef = useRef(setSelectedEntries);

  useEffect(() => {
    dataRef.current = data;
    keyExtractorRef.current = keyExtractor;
    setSelectedEntriesRef.current = setSelectedEntries;
  });

  useEffect(() => {
    setSelectionList([]);
  }, [data]);

  useEffect(() => {
    if (setSelectedEntriesRef.current) {
      const newSelectedEntries = dataRef.current.filter((v) =>
        selectionList.includes(keyExtractorRef.current(v)),
      );
      setSelectedEntriesRef.current(newSelectedEntries);
    }
  }, [selectionList]);

  function selectAll() {
    setSelectionList(data.map((v) => keyExtractor(v)));
  }

  const amountOfSelectedEntries: "some" | "all" | "none" =
    selectionList.length === 0
      ? "none"
      : selectionList.length === data.length
        ? "all"
        : "some";

  function togglePlaylistSelection(identifier: string) {
    setSelectionList((prev: string[]) => {
      if (prev.includes(identifier)) {
        setLatestSelectedIdentifier(undefined);

        return prev.filter((i) => i !== identifier);
      } else {
        if (
          isHotkeyPressed("shift") &&
          latestSelectedIdentifier !== undefined
        ) {
          const identifiersToAdd = findAllIdentifiersBetweenInclusive(
            latestSelectedIdentifier,
            identifier,
          );
          setLatestSelectedIdentifier(undefined);
          return [...prev, ...identifiersToAdd];
        } else {
          setLatestSelectedIdentifier(identifier);
          return [...prev, identifier];
        }
      }
    });
  }

  function findAllIdentifiersBetweenInclusive(
    latestSelectedIdentifier: string,
    identifier: string,
  ): readonly string[] {
    let isBetweenSelection = false;
    const selection: string[] = [];

    data.forEach((d) => {
      const anIdentifier = keyExtractor(d);
      if (
        anIdentifier === latestSelectedIdentifier ||
        anIdentifier === identifier
      ) {
        selection.push(anIdentifier);
        isBetweenSelection = !isBetweenSelection;
      }
      if (isBetweenSelection) {
        selection.push(anIdentifier);
      }
    });

    return selection;
  }

  return (
    <>
      <StickyMainHeader>
        {headerFirstRow}

        <ListGroup
          horizontal
          style={{ flex: "1 1 auto" }}
          className="d-lg-flex mt-2"
        >
          <HeaderEntryCheckboxColumn
            setSelectionList={setSelectionList}
            amountOfSelectedEntries={amountOfSelectedEntries}
            selectAllVisible={selectAll}
          />

          <ListGroupItem className="w-100 border-0 bg-light">
            <Row>{headerColumns}</Row>
          </ListGroupItem>
        </ListGroup>
      </StickyMainHeader>

      {loading ? (
        <LoadingIndicator text="Daten werden geladen..." />
      ) : (
        data.map((v: T) => (
          <Entry
            key={keyExtractor(v)}
            dataCy={CyPlaylistIndexTable.PlaylistSummary}
            identifier={keyExtractor(v)}
            toggleSelection={togglePlaylistSelection}
            isSelected={selectionList.includes(keyExtractor(v))}
            to={linkBuilder ? linkBuilder(v) : undefined}
            onClick={() => (onClick ? onClick(v) : undefined)}
          >
            {renderElement(v)}
          </Entry>
        ))
      )}
    </>
  );
}
